import { Suspense } from "react";
import { createRootRouteWithContext, createRoute, createRouter, Outlet, redirect } from "@tanstack/react-router";
import type { QueryClient } from "@tanstack/react-query";
import { LoadingState } from "@signet/ui";
import { api, ApiError, canAnyIn, landingFor, type BoundMe, type Me, type Needs } from "@signet/web-kit";
import { webModules } from "../../../modules/registry.web.ts";
import { Shell } from "./shell/shell.tsx";
import { BoundShell } from "./shell/bound-shell.tsx";
import { LoginPage } from "./auth/login.tsx";
import { NowPage } from "./pages/now.tsx";
import { PeoplePage } from "./pages/people.tsx";
import { RolesPage } from "./pages/roles.tsx";
import { UsagePage } from "./pages/usage/index.tsx";
import { USAGE_NEEDS } from "./pages/usage/needs.ts";
import { SettingsPage } from "./pages/settings.tsx";
import { NotFound, Forbidden } from "./pages/not-found.tsx";
import { QuickBar } from "./quick/quick-bar.tsx";

/* ──────────────────────────────────────────────────────────────────────────
   Routes: sign-in outside the shell, a colleague's pages inside it, and a
   bound account's one page (a creator's own sign-in) in a frame of its own.

   The shell route loads /api/me before anything renders, so every page can
   assume a person. A page whose permission the person lacks renders
   Forbidden rather than a half-empty screen that errors as it loads.
   ────────────────────────────────────────────────────────────────────────── */

type Context = { queryClient: QueryClient };

const root = createRootRouteWithContext<Context>()({ component: Outlet, notFoundComponent: NotFound });

const login = createRoute({
  getParentRoute: () => root,
  path: "/login",
  validateSearch: (raw: Record<string, unknown>) => ({ next: typeof raw.next === "string" ? raw.next : undefined }),
  component: LoginPage,
});

/** Where each kind of bound account lives, by the module that binds it. */
const boundPageOf = (module: string) => webModules.find((mod) => mod.id === module)?.bound ?? null;

/**
 * Every page a bound account has: the binding module's own single page, or
 * the pages modules add to its portal (`portal.for`), with their full paths.
 * Only modules the API says serve the account (`/api/bound`'s `modules`)
 * count, so the web and the server cannot disagree about what it may open.
 */
function portalPagesOf(module: string, serving: readonly string[] = [module], under?: string) {
  const root = under ?? boundPageOf(module)?.path;
  if (!root) return [];
  const full = (path: string) => (path ? `${root}/${path}`.replace(/\/+/g, "/") : root);
  return webModules
    .filter((mod) => serving.includes(mod.id) && mod.portal?.for === module)
    .flatMap((mod) => mod.portal!.pages ?? [])
    .map((page) => ({ ...page, to: full(page.path) }))
    .sort((a, b) => a.order - b.order);
}

/** The welcome tour's steps from every module serving the binding, in order, each with its page's full path. */
function portalTourOf(module: string, serving: readonly string[] = [module], under?: string) {
  const root = under ?? boundPageOf(module)?.path;
  if (!root) return [];
  const full = (path: string) => (path ? `${root}/${path}`.replace(/\/+/g, "/") : root);
  return webModules
    .filter((mod) => serving.includes(mod.id) && mod.portal?.for === module)
    .flatMap((mod) => mod.portal!.tour ?? [])
    .map(({ page, ...step }) => ({ ...step, to: page === undefined ? undefined : full(page) }))
    .sort((a, b) => a.order - b.order);
}

const shell = createRoute({
  getParentRoute: () => root,
  id: "shell",
  beforeLoad: async ({ context, location }) => {
    let me: Me;
    try {
      me = await context.queryClient.fetchQuery({ queryKey: ["me"], queryFn: () => api<Me>("/api/me"), staleTime: 60_000 });
    } catch (error) {
      // 404 from /api/me is a signed-in account that is not a colleague (a
      // creator's own sign-in): the API closes every staff path to it. Send it
      // to its own page rather than to sign-in, where it would loop.
      if (error instanceof ApiError && error.status === 404) {
        const who = await context.queryClient.fetchQuery({ queryKey: ["bound"], queryFn: () => api<BoundMe>("/api/bound"), staleTime: 60_000 }).catch(() => null);
        const page = who ? boundPageOf(who.module) : null;
        if (page) throw redirect({ to: page.path as never });
      }
      throw redirect({ to: "/login", search: { next: location.pathname === "/" ? undefined : location.href } });
    }
    return { me };
  },
  component: Shell,
});

/*
 * A bound account's frame: its own page and nothing else. The API decides
 * what it may reach; this only decides what to draw, so a colleague who
 * lands here is sent home and a bound account is always on its one page.
 */
const bound = createRoute({
  getParentRoute: () => root,
  id: "bound",
  beforeLoad: async ({ context, location }) => {
    let who: BoundMe;
    try {
      who = await context.queryClient.fetchQuery({ queryKey: ["bound"], queryFn: () => api<BoundMe>("/api/bound"), staleTime: 60_000 });
    } catch (error) {
      if (error instanceof ApiError && error.status === 404) throw redirect({ to: "/" });
      throw redirect({ to: "/login", search: { next: location.href } });
    }
    const page = boundPageOf(who.module);
    const paths = new Set([...(page?.component ? [page.path] : []), ...portalPagesOf(who.module, who.modules).map((p) => p.to)]);
    const here = location.pathname.replace(/\/+$/, "") || "/";
    if (page && !paths.has(here)) throw redirect({ to: page.path as never });
    return { who };
  },
  component: function BoundFrame() {
    const { who } = bound.useRouteContext() as { who: BoundMe };
    const Frame = boundPageOf(who.module)?.frame;
    if (!Frame) return <BoundShell who={who} />;
    const pages = portalPagesOf(who.module, who.modules).map(({ to, path, label, short, icon, order }) => ({ to, path, label, short, icon, order }));
    return <Suspense fallback={loading}><Frame pages={pages} tour={portalTourOf(who.module, who.modules)} api="/api/bound" preview={null} /></Suspense>;
  },
});

const boundPages = webModules.flatMap((mod) => {
  if (!mod.bound) return [];
  const single = mod.bound.component ? [createRoute({ getParentRoute: () => bound, path: mod.bound.path, component: page(mod.bound.component) })] : [];
  // Every module's portal pages for this binding, whatever the API says later:
  // routes are built once, and beforeLoad sends an account away from any
  // page its server does not serve.
  const portal = portalPagesOf(mod.id, webModules.map((m) => m.id)).map((p) =>
    createRoute({ getParentRoute: () => bound, path: p.to, component: page(p.component) }),
  );
  return [...single, ...portal];
});

/*
 * A colleague previewing a bound account's pages: /preview/<module>/<record>,
 * the same frame and pages the account sees, reading the same routes through
 * the API's read-only preview mount. Outside the staff shell, like the
 * account's own, but only for somebody signed in as a colleague; the server
 * decides whether they may see this record (404 otherwise).
 */
const previewRoots = webModules.flatMap((mod) => {
  if (!mod.bound?.frame || !mod.bound.preview) return [];
  const Frame = mod.bound.frame;
  const everyone = webModules.map((m) => m.id);
  const parent = createRoute({
    getParentRoute: () => root,
    path: `/preview/${mod.id}/$record`,
    // Where Back to Signet goes: the page the preview was opened from, if it
    // is one of ours, else People.
    validateSearch: (raw: Record<string, unknown>) => ({
      from: typeof raw.from === "string" && /^\/(?!\/)/.test(raw.from) ? raw.from : undefined,
    }),
    beforeLoad: async ({ context, location }) => {
      try {
        await context.queryClient.fetchQuery({ queryKey: ["me"], queryFn: () => api<Me>("/api/me"), staleTime: 60_000 });
      } catch {
        throw redirect({ to: "/login", search: { next: location.href } });
      }
    },
    component: function PreviewFrame() {
      const { record } = parent.useParams() as { record: string };
      const { from } = parent.useSearch() as { from?: string };
      const name = record.toUpperCase();
      const under = `/preview/${mod.id}/${encodeURIComponent(name)}`;
      const pages = portalPagesOf(mod.id, everyone, under).map(({ to, path, label, short, icon, order }) => ({ to, path, label, short, icon, order }));
      return (
        <Suspense fallback={loading}>
          <Frame pages={pages} tour={portalTourOf(mod.id, everyone, under)} api={`/api/preview/${mod.id}/${encodeURIComponent(name)}`} preview={{ record: name, exit: from ?? "/people" }} />
        </Suspense>
      );
    },
  });
  const children = portalPagesOf(mod.id, everyone).map((p) =>
    createRoute({ getParentRoute: () => parent, path: p.path || "/", component: page(p.component) }),
  );
  return [parent.addChildren(children)];
});

// Module pages load when first opened (web-kit lazy.ts); this is what shows
// for the moment one is being fetched.
const loading = <LoadingState />;

function page(Page: React.ComponentType) {
  return function BoundPage() {
    return <Suspense fallback={loading}><Page /></Suspense>;
  };
}

// A list means any one of them is enough: API/AI Usage opens for whoever can
// see one of its sections, and each section checks its own.
function guarded(needs: Needs, Page: React.ComponentType) {
  return function GuardedPage() {
    const me = shell.useRouteContext().me as Me;
    return canAnyIn(me, needs) ? <Suspense fallback={loading}><Page /></Suspense> : <Forbidden />;
  };
}

const core = [
  createRoute({
    getParentRoute: () => shell,
    path: "/",
    // Without the Now permission the home address opens their first page instead.
    beforeLoad: ({ context }) => {
      const to = landingFor((context as { me?: Me }).me, webModules);
      if (to !== "/") throw redirect({ to: to as never, replace: true });
    },
    component: NowPage,
  }),
  createRoute({ getParentRoute: () => shell, path: "/people", component: guarded("signet.people:view", PeoplePage) }),
  createRoute({ getParentRoute: () => shell, path: "/roles", component: guarded("signet.roles:view", RolesPage) }),
  createRoute({ getParentRoute: () => shell, path: "/usage", component: guarded(USAGE_NEEDS, UsagePage) }),
  createRoute({ getParentRoute: () => shell, path: "/settings", component: SettingsPage }),
];

const moduleRoutes = webModules.flatMap((mod) =>
  mod.routes.map((route) =>
    createRoute({
      getParentRoute: () => shell,
      path: route.path,
      validateSearch: route.validateSearch ?? ((raw: Record<string, unknown>) => raw),
      component: guarded(route.needs, route.component),
    }),
  ),
);

// The desktop quick bar's window (apps/desktop/main.mjs). Outside the shell:
// it has no bar of its own, and signed out it offers sign-in in the main
// window rather than turning the little window into the sign-in page.
const quick = createRoute({ getParentRoute: () => root, path: "/quick", component: QuickBar });

const tree = root.addChildren([login, quick, shell.addChildren([...core, ...moduleRoutes]), bound.addChildren(boundPages), ...previewRoots]);

export const router = createRouter({
  routeTree: tree,
  context: { queryClient: undefined! },
  defaultPreload: "intent",
  scrollRestoration: true,
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
